import React, { useState, useEffect } from "react";
import Input from "../../../ui/common/Input";
import TextArea from "../../../ui/common/TextArea";
import Button from "../../../ui/common/Button";
import "./CustomEndpointModal.scss";

interface CustomEndpoint {
  serviceName: string;
  endpoint: string;
  apiKey?: string;
  bearerToken?: string;
  payloadMapping?: string;
}

interface CustomEndpointModalProps {
  serviceName: string;
  existingEndpoint?: CustomEndpoint;
  onSave: (endpoint: CustomEndpoint) => void;
  onClose: () => void;
  onDelete?: (serviceName: string) => void;
}

const CustomEndpointModal: React.FC<CustomEndpointModalProps> = ({
  serviceName,
  existingEndpoint,
  onSave,
  onClose,
  onDelete,
}) => {
  const [endpoint, setEndpoint] = useState(existingEndpoint?.endpoint || "");
  const [apiKey, setApiKey] = useState(existingEndpoint?.apiKey || "");
  const [bearerToken, setBearerToken] = useState(existingEndpoint?.bearerToken || "");
  const [payloadMapping, setPayloadMapping] = useState(() => {
    if (!existingEndpoint?.payloadMapping) return "";
    if (typeof existingEndpoint.payloadMapping === "string") {
      return existingEndpoint.payloadMapping;
    }
    return JSON.stringify(existingEndpoint.payloadMapping, null, 2);
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateUrl = (url: string): boolean => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validatePayloadMapping = (mapping: string): boolean => {
    if (!mapping.trim()) return true;
    try {
      JSON.parse(mapping);
      return true;
    } catch {
      return false;
    }
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setApiKey(value);
    if (value) {
      setBearerToken("");
    }
  };

  const handleBearerTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBearerToken(value);
    if (value) {
      setApiKey("");
    }
  };

  const handleSave = () => {
    const newErrors: { [key: string]: string } = {};

    if (!validateUrl(endpoint)) {
      newErrors.endpoint = "Please enter a valid HTTPS URL";
    }

    if (payloadMapping && !validatePayloadMapping(payloadMapping)) {
      newErrors.payloadMapping = "Please enter valid JSON";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const customEndpoint: CustomEndpoint = {
      serviceName,
      endpoint,
    };

    if (apiKey) {
      customEndpoint.apiKey = apiKey;
    }

    if (bearerToken) {
      customEndpoint.bearerToken = bearerToken;
    }

    if (payloadMapping.trim()) {
      try {
        customEndpoint.payloadMapping = JSON.parse(payloadMapping);
      } catch {
        customEndpoint.payloadMapping = payloadMapping;
      }
    }

    onSave(customEndpoint);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure to remove this configuration, this can't be undone")) {
      onDelete?.(serviceName);
      onClose();
    }
  };

  return (
    <div className="custom-endpoint-modal-overlay" onClick={onClose}>
      <div className="custom-endpoint-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Configure Custom AI-service Endpoint for "{serviceName}" goal</h3>
        
        <div className="custom-endpoint-form">
          <Input
            label="Service Name"
            value={serviceName}
            disabled
            separateLines
            fullWidth
          />

          <Input
            label="Endpoint URL"
            value={endpoint}
            onChange={(e) => {
              setEndpoint(e.target.value);
              setErrors({ ...errors, endpoint: "" });
            }}
            separateLines
            fullWidth
            placeholder="https://example.com/endpoint"
          />
          {errors.endpoint && <div className="error-message">{errors.endpoint}</div>}

          <Input
            label="API Key"
            value={apiKey}
            onChange={handleApiKeyChange}
            separateLines
            fullWidth
            disabled={!!bearerToken}
            placeholder="Optional - mutually exclusive with Bearer Token"
          />

          <Input
            label="Bearer Token"
            value={bearerToken}
            onChange={handleBearerTokenChange}
            separateLines
            fullWidth
            disabled={!!apiKey}
            placeholder="Optional - mutually exclusive with API Key"
          />

          <TextArea
            label="Payload Mapping (JSON)"
            onChange={(e) => {
              setPayloadMapping(e.target.value);
              setErrors({ ...errors, payloadMapping: "" });
            }}
            placeholder='[{"requestField": "request_id", "sourceField": "requestId"}]'
            rows={8}
          >
            {payloadMapping}
          </TextArea>
          {errors.payloadMapping && <div className="error-message">{errors.payloadMapping}</div>}
        </div>

        <div className="custom-endpoint-actions">
          {existingEndpoint && onDelete && (
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <div className="custom-endpoint-actions-right">
            <Button variant="danger" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSave}>
              OK
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomEndpointModal;
